import React from 'react';
import { Link } from 'react-router-dom';
import type { ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { legalDocumentLink, legalIdentityParts, type LegalDocumentKind, type ShopLegal } from '../legal';
import LogoMain from './LogoMain';

/** Small caps label — the one type style that carries a venue's accent on request. */
export const SmallCaps: React.FC<{
  theme: ShopTheme;
  children: React.ReactNode;
  accent?: boolean;
  className?: string;
}> = ({ theme, children, accent = false, className = '' }) => (
  <div
    className={className}
    style={{
      font: `600 11px/1 ${theme.fontFamily}`,
      letterSpacing: '.2em',
      textTransform: 'uppercase',
      color: accent ? theme.accent : theme.label,
    }}
  >
    {children}
  </div>
);

/** A ruled section heading: title left, a quiet qualifier right, hairline underneath. */
export const SectionHeading: React.FC<{
  theme: ShopTheme;
  title: string;
  aside?: string;
  className?: string;
}> = ({ theme, title, aside, className = '' }) => (
  <div
    className={`flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 pb-3.5 ${className}`}
    style={{ borderBottom: `1px solid ${theme.hairline}` }}
  >
    <h2 style={{ font: `500 clamp(20px, 3vw, 24px)/1.2 ${theme.fontFamily}`, letterSpacing: '-.01em', color: theme.ink, margin: 0 }}>
      {title}
    </h2>
    {aside && <span style={{ font: `400 13px/1.4 ${theme.fontFamily}`, color: theme.label }}>{aside}</span>}
  </div>
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & { theme: ShopTheme };

/** The primary call to action — one of the three places the venue's colour appears. */
export const AccentButton: React.FC<ButtonProps> = ({ theme, className = '', style, disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 transition-opacity disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
    style={{
      padding: '15px 26px',
      background: theme.accent,
      color: theme.accentText,
      border: 'none',
      borderRadius: theme.radius,
      font: `600 15px/1 ${theme.fontFamily}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  />
);

/** Everything that is not the primary action: outlined, ink on paper. */
export const QuietButton: React.FC<ButtonProps> = ({ theme, className = '', style, disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
    style={{
      padding: '14px 22px',
      background: theme.surface,
      color: theme.ink,
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: theme.radius,
      font: `500 15px/1 ${theme.fontFamily}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  />
);

/** Ink-filled button, used where the accent would compete (e.g. "Book a table"). */
export const InkButton: React.FC<ButtonProps> = ({ theme, className = '', style, disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 transition-opacity disabled:cursor-not-allowed disabled:opacity-45 ${className}`}
    style={{
      padding: '14px 22px',
      background: theme.ink,
      color: theme.paper,
      border: 'none',
      borderRadius: theme.radius,
      font: `600 14px/1 ${theme.fontFamily}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...style,
    }}
  />
);

export const VenueLogo: React.FC<{
  theme: ShopTheme;
  venueName?: string;
  height?: number;
  /** Shown when the venue has neither a logo nor a name, e.g. on the bare domain root. */
  fallbackName?: string;
}> = ({ theme, venueName, height = 30, fallbackName = '' }) => {
  if (theme.logoUrl) {
    return (
      <img
        src={theme.logoUrl}
        alt={venueName || 'Logo'}
        className="block h-auto w-auto object-contain"
        style={{ maxHeight: height, maxWidth: theme.logoWidth }}
      />
    );
  }
  return (
    <span style={{ font: `500 17px/1 ${theme.fontFamily}`, letterSpacing: '-.01em', color: theme.ink }}>
      {venueName || fallbackName}
    </span>
  );
};

export const ShopHeader: React.FC<{
  theme: ShopTheme;
  venueName?: string;
  onHome?: () => void;
  onCheckVoucher?: () => void;
}> = ({ theme, venueName, onHome, onCheckVoucher }) => {
  const { t } = useI18n();
  return (
  <header
    className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10 sm:py-[22px]"
    style={{ borderBottom: `1px solid ${theme.hairline}` }}
  >
    <button
      type="button"
      onClick={onHome}
      className="min-w-0 truncate text-left"
      style={{ cursor: onHome ? 'pointer' : 'default' }}
    >
      <VenueLogo theme={theme} venueName={venueName} />
    </button>
    <nav className="flex shrink-0 items-center gap-7" style={{ font: `400 14px/1 ${theme.fontFamily}` }}>
      <button type="button" onClick={onHome} className="hidden sm:inline" style={{ color: theme.body }}>
        {t('nav.gift_vouchers')}
      </button>
      <button type="button" onClick={onCheckVoucher} className="whitespace-nowrap" style={{ color: theme.label }}>
        {t('nav.check_voucher')}
      </button>
    </nav>
  </header>
  );
};

/** One footer link, styled the same whether it leaves the shop or not. */
const LegalLink: React.FC<{
  theme: ShopTheme;
  kind: LegalDocumentKind;
  locationId?: string;
  legal?: ShopLegal;
  label: string;
}> = ({ theme, kind, locationId, legal, label }) => {
  const link = legalDocumentLink(kind, locationId, legal);
  if (!link) return null;

  const style = { font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.body } as const;
  const className = 'underline underline-offset-2 decoration-[0.5px]';

  return link.external ? (
    <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} style={style}>
      {label}
    </a>
  ) : (
    <Link to={link.href} className={className} style={style}>
      {label}
    </Link>
  );
};

export const ShopFooter: React.FC<{
  theme: ShopTheme;
  venueName?: string;
  venueAddress?: string;
  /**
   * The trading company behind the shop. When it is there the footer carries a second
   * row — statutory name, company register number, VAT — and links the terms and the
   * privacy statement, which is what a payment provider checks before going live.
   */
  legal?: ShopLegal;
  /** Needed to link the hosted documents when the venue has no pages of its own. */
  locationId?: string;
}> = ({ theme, venueName, venueAddress, legal, locationId }) => {
  const { t } = useI18n();
  const identityParts = legalIdentityParts(legal, t('legal.vat'));
  const hasLegalRow =
    identityParts.length > 0 ||
    !!legalDocumentLink('terms', locationId, legal) ||
    !!legalDocumentLink('privacy', locationId, legal);

  return (
    <footer className="mt-auto px-5 py-6 sm:px-10" style={{ borderTop: `1px solid ${theme.hairline}` }}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <span style={{ font: `400 13px/1.5 ${theme.fontFamily}`, color: theme.label }}>
          {[venueName, venueAddress].filter(Boolean).join(' · ')}
        </span>
        <a
          href="https://10seat.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2.5"
          style={{ font: `400 12px/1 ${theme.fontFamily}`, color: theme.label }}
        >
          {t('common.powered_by')}
          <LogoMain size="sm" className="opacity-75" />
        </a>
      </div>

      {hasLegalRow && (
        <div
          className="mt-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 pt-4"
          style={{ borderTop: `1px solid ${theme.hairlineSoft}` }}
        >
          {identityParts.length > 0 && (
            <span style={{ font: `400 13px/1.6 ${theme.fontFamily}`, color: theme.label }}>
              {identityParts.join(' · ')}
            </span>
          )}
          <span className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <LegalLink theme={theme} kind="terms" locationId={locationId} legal={legal} label={t('legal.terms')} />
            <LegalLink theme={theme} kind="privacy" locationId={locationId} legal={legal} label={t('legal.privacy')} />
          </span>
        </div>
      )}
    </footer>
  );
};
